import { useEffect } from "react";
import { useDesign } from "./store";
import {
  initPresence,
  setSelection,
  setDragging,
  teardownPresence,
} from "./presence";
import { useAuth } from "./auth";
import type { PresenceSelection } from "./sharedRoomTypes";

/**
 * React-side bridge between the {@link useDesign} store's selection state
 * and the RTDB presence layer. Mount once at the app root inside any
 * provider tree that has access to the shared-room id.
 *
 * Responsibilities:
 *   - When a user enters/leaves a shared room, init / teardown the
 *     presence session.
 *   - Mirror their current selection (and any active drag preview) into
 *     RTDB on every change so collaborators see colored selection halos
 *     and live drag ghosts.
 *
 * Cursor positions come from the canvases themselves (they own the
 * pixel-to-room conversion via `useFitToRoom`), so this hook doesn't
 * touch them.
 */
export function usePresenceBridge(): void {
  const auth = useAuth();
  const sharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const selectedFurnitureId = useDesign((s) => s.selectedFurnitureId);
  const selectedMountId = useDesign((s) => s.selectedMountId);
  const selectedOpeningId = useDesign((s) => s.selectedOpeningId);
  const selectedRoomWallIndex = useDesign((s) => s.selectedRoomWallIndex);
  const selectedRoomVertexIndex = useDesign((s) => s.selectedRoomVertexIndex);

  const authUid = auth.user?.uid ?? null;

  // Init/teardown when the user enters or leaves a shared room. Once the
  // session is up we immediately push the *current* selection so that a
  // user who already had something selected before opening the shared
  // room appears with that halo to their collaborators (rather than
  // having to nudge selection before the first broadcast).
  useEffect(() => {
    if (!sharedRoomId || !auth.user) return;
    const ok = initPresence({ roomId: sharedRoomId, user: auth.user });
    if (!ok) return;
    pushCurrentSelection();
    return () => {
      teardownPresence();
    };
    // `pushCurrentSelection` reads fresh state from the store at call
    // time, so we deliberately don't include the selection fields here —
    // the selection-mirror effect below handles ongoing updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedRoomId, auth.user]);

  // Mirror selection into RTDB on every change. Includes `authUid` so a
  // late-resolving auth state (Firebase still negotiating when the
  // shared room loads) still triggers a write once presence is alive.
  useEffect(() => {
    if (!sharedRoomId || !authUid) return;
    pushCurrentSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sharedRoomId,
    authUid,
    selectedFurnitureId,
    selectedMountId,
    selectedOpeningId,
    selectedRoomWallIndex,
    selectedRoomVertexIndex,
  ]);

  // Clear any lingering drag preview when leaving the shared room.
  useEffect(() => {
    if (!sharedRoomId) {
      setDragging(null);
    }
  }, [sharedRoomId]);

  function pushCurrentSelection(): void {
    const s = useDesign.getState();
    const sel: PresenceSelection = {
      furnitureId: s.selectedFurnitureId,
      mountId: s.selectedMountId,
      openingId: s.selectedOpeningId,
      roomWallIndex: s.selectedRoomWallIndex,
      roomVertexIndex: s.selectedRoomVertexIndex,
    };
    const anySelection =
      sel.furnitureId ||
      sel.mountId ||
      sel.openingId ||
      sel.roomWallIndex !== null ||
      sel.roomVertexIndex !== null;
    setSelection(anySelection ? sel : null);
  }
}
